import React from 'react'
import { Box, Typography, SvgIcon } from '@mui/material'
import { useTranslation } from 'react-i18next'
import LockAccessIcon from '@hapana/shared/web/styles/assets/svgs/lock-access.svg'
import type { FeatureCheckResult } from '@hapana/shared/schemas/entitlements/entitlement-result.schema'
import { ENTITLEMENT_TESTIDS } from '@hapana/shared/constants/entitlements-testids.constants'
import Button from '../button/button.component'
import { featureDisabledStyles as styles } from './feature-disabled.styles'

export interface FeatureDisabledProps {
  /** The result from entitlements.checkFeature() */
  checkResult: FeatureCheckResult
  /** Feature display name for the message */
  featureName: string
  /** Called when the user clicks the upgrade button. If not provided, button is hidden. */
  onUpgrade?: () => void
  /** Label for the action button. Defaults to "Upgrade Plan" or "Contact your admin" */
  actionLabel?: string
}

/**
 * Full-screen "you cannot use this" state for a route the location's plan does not include.
 *
 * The message comes from `checkFeature`, which names the plan or theme that unlocks the feature.
 */
export const FeatureDisabled: React.FC<FeatureDisabledProps> = ({
  checkResult,
  featureName,
  onUpgrade,
  actionLabel,
}) => {
  const { t } = useTranslation('common')

  const upgradeMessage = checkResult.upgradeInfo?.message ?? ''
  const defaultActionLabel = onUpgrade
    ? t('entitlements.featureDisabled.upgradePlan')
    : t('entitlements.featureDisabled.contactAdminToUpgrade')

  return (
    <Box sx={styles.root} data-testid={ENTITLEMENT_TESTIDS.featureDisabled}>
      <Box sx={styles.container}>
        <Box sx={styles.iconContainer}>
          <SvgIcon sx={styles.icon} component={LockAccessIcon} />
        </Box>
        <Typography typography="header1" color="gray.64">
          {t('entitlements.featureDisabled.title', { feature: featureName })}
        </Typography>
        <Typography typography="large" color="gray.64" sx={styles.description}>
          {upgradeMessage ||
            t('entitlements.featureDisabled.description', { feature: featureName })}
        </Typography>
        {onUpgrade && <Button label={actionLabel || defaultActionLabel} onClick={onUpgrade} />}
        {!onUpgrade && (
          <Typography typography="body2" color="gray.40">
            {defaultActionLabel}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
